import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, map, Subject } from 'rxjs';
import { startWith, toArray, filter } from 'rxjs/operators';

@Component({
  selector: 'app-warehouse-creator',
  templateUrl: './warehouse-creator.component.html',
  styleUrls: ['./warehouse-creator.component.scss']
})
export class WarehouseCreatorComponent implements OnInit {

  labelOptions = ['Numbers', 'Letters'];

  rowNamingOption: string = ''
  sectionNamingOption: string = ''

  warehouse_form!: FormGroup

  rows: any[] = [];
  sections: any[] = [];
  areas: any[] = [];
  positions: any[] = [];

  contactList: any[] = [];
  filteredContactList: Observable<string[]> | undefined;
  contactFullList: any[] | undefined;

  contact_selected_uuid: string = '';

  constructor(private http: HttpClient, private formBuilder: FormBuilder, private router: Router) {
    this.warehouse_form = this.formBuilder.group({
      contact_organization: ['', Validators.required],
      contact_uuid: ['', Validators.required],
      inventory_warehouse_name: ['', Validators.required],
      no_rows: ['', Validators.required],
      no_sections: ['', Validators.required],
      no_levels: ['', Validators.required],
      no_spots: ['', Validators.required],
      rowNamingOption: ['', Validators.required],
      sectionNamingOption: ['', Validators.required]
    })
  }

  ngOnInit() {

    //Populate the contact address list in the switching contact feature
    this.warehouse_form.get('contact_organization')?.valueChanges.subscribe((data) => {
        const index = this.contactFullList?.findIndex((item: any) => item.label === data);
        if (index !== -1 && this.contactFullList && index !== undefined) {
          this.contact_selected_uuid = this.contactFullList[index]?.value;
          this.warehouse_form.get('contact_uuid')?.setValue(this.contact_selected_uuid);
          console.log('contact_uuid: ' + this.warehouse_form.get('contact_uuid')?.value);
        }
    });

    //Filter contact list and Initialize contact list
    this.getContactList().subscribe((data) => this.contactList = data);
    this.filteredContactList = this.warehouse_form.get('contact_organization')?.valueChanges
      .pipe(
        startWith(''),
        filter(value => value.length >= 3), // Only proceed if the input length is 3 or more
        map(contactList => contactList ? this.filterContactList(contactList) : this.contactList.slice())
      );

  }

  submitForm() {
    // generate Row, Section, and Position data
    this.generateRows()
    this.generateSections()
    this.generatePositions()

    // remove row naming and section naming option fields
    delete this.warehouse_form.value.rowNamingOption
    delete this.warehouse_form.value.sectionNamingOption

    var postData = {
      warehouses: this.warehouse_form.value,
      rows: this.rows,
      areas: this.areas,
      positions: this.positions
    }
    console.log('POST DATA', postData)

    const url = '/api/inventory/inventory_warehouse_create.php'
    return this.http.post<any>(url, postData)
      .subscribe(response => {
        console.log('Post Response: ', response)
        this.router.navigate(['/inventory']);
      })

  }

  generateRows() {
    const noRows = this.warehouse_form.get('no_rows')?.value;
    const rowNamingOption = this.rowNamingOption

    this.rows = [];
    let rowIdentifierStart;

    if (rowNamingOption === 'Letters') {
      rowIdentifierStart = 65; // ASCII code for 'A' in order to label rows with letters

      for (let i = 0; i < noRows; i++) {
        let rowIdentifier = rowIdentifierStart + Math.floor(i / 26); // Dividing by 26 to determine the letter group
        let rowName = '';

        if (rowIdentifierStart === 65) {
          // determine row name first letter, either 'A', 'B', for rows 1-26, or 'AA', 'AB', for rows > 26
          let firstLetter = Math.floor(i / 26) > 0 ? String.fromCharCode(65 + (Math.floor(i / 26) - 1) % 26) : '';
          rowName += firstLetter + String.fromCharCode(65 + (i % 26));
        } else {
          // row name is just the iterated number
          rowName += rowIdentifier;
        }

        const rowObject = {
          inventory_warehouse_name: this.warehouse_form.get('inventory_warehouse_name')?.value,
          inventory_row: rowName,
          inventory_row_name: `Row ${rowName}`
        };
        this.rows.push(rowObject);
      }

    } else {
      // if row naming option is standard numerical, just iterate normally from 1
      for (let i = 1; i <= noRows; i++) {
        const rowObject = {
          inventory_warehouse_name: this.warehouse_form.get('inventory_warehouse_name')?.value,
          inventory_row: i.toString(),
          inventory_row_name: `Row ${i}`
        };
        this.rows.push(rowObject);
      }
    }
  }

  generateSections() {
    const noSections = this.warehouse_form.get('no_sections')?.value;
    const sectionNamingOption = this.sectionNamingOption

    this.sections = [];
    let sectionIdentifierStart;

    if (sectionNamingOption === 'Letters') {
      sectionIdentifierStart = 65; // ASCII code for 'A' in order to label sections with letters

      for (let i = 0; i < noSections; i++) {
        let sectionIdentifier = sectionIdentifierStart + Math.floor(i / 26); // Dividing by 26 to determine the letter group
        let sectionName = '';

        if (sectionIdentifierStart === 65) {
          // determine sections name first letter, either 'A', 'B', for sections 1-26, or 'AA', 'AB', for sections > 26
          let firstLetter = Math.floor(i / 26) > 0 ? String.fromCharCode(65 + (Math.floor(i / 26) - 1) % 26) : '';
          sectionName += firstLetter + String.fromCharCode(65 + (i % 26));
        } else {
          // section name is just the iterated number
          sectionName += sectionIdentifier;
        }

        const sectionObject = {
          inventory_warehouse_name: this.warehouse_form.get('inventory_warehouse_name')?.value,
          inventory_section: sectionName,
          inventory_section_name: `Section ${sectionName}`
        };
        this.sections.push(sectionObject);
      }

    } else {
      // if row naming option is standard numerical, just iterate normally from 1
      for (let i = 1; i <= noSections; i++) {
        const sectionObject = {
          inventory_warehouse_name: this.warehouse_form.get('inventory_warehouse_name')?.value,
          inventory_section: i.toString(),
          inventory_section_name: `Section ${i}`
        };
        this.sections.push(sectionObject);
      }
    }
  }

  generatePositions() {
    this.positions = [];

    for (let rowObj of this.rows) {
      const row = rowObj.inventory_row;
      const warehouseName = rowObj.inventory_warehouse_name;
      const contactOrganization = this.warehouse_form.get('contact_organization')?.value;

      const noLevels = this.warehouse_form.get('no_levels')?.value;
      const noSpots = this.warehouse_form.get('no_spots')?.value;

      for (let sectionObj of this.sections) { // Iterate over sections array
        const section = sectionObj.inventory_section;
        for (let level = 1; level <= noLevels; level++) {
          const area_name = `Row ${row} Section ${section} Level ${level}`
          for (let spot = 1; spot <= noSpots; spot++) {
            const positionObject = {
              inventory_row_name: `Row ${row}`,
              inventory_warehouse_name: warehouseName,
              // contact_organization: contactOrganization,
              row: row,
              section: section,
              level: level.toString(),
              spot: spot.toString(),
              inventory_area_name: area_name,
              inventory_position_name: `Row ${row} Section ${section} Level ${level} Spot ${spot}`
            };
            this.positions.push(positionObject);
          }
          const area_object = {
            inventory_area_name: area_name,
            inventory_row_name: `Row ${row}`,
            inventory_warehouse_name: warehouseName,
            row: row,
            section: section,
            level: level.toString()
          }
          this.areas.push(area_object) // populate areas array
        }
      }
    }
  }

  getContactList(): Observable<string[]> {
    return this.http.get<any[]>('/api/inventory/inventory_contact_autofill.php').pipe(
      map(response => {
        if (response) {
          this.contactFullList = response;
          return response.map(item => item.label);
        } else {
          return [];
        }
      })
    );
  }

  filterContactList(name: string) {
    return this.contactList.filter(contactList =>
      contactList.toLowerCase().indexOf(name.toLowerCase()) === 0);
  }

}
